import { memo } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import { Typography } from '@material-ui/core';
import UserAvatar from '../../../../userAvatar';
import Tool from '../../tool';
import ReasoningBlock from '../../reasoningBlock';
import AssistantAvatar from '../assistantAvatar';
import {
  LEVELING_AGENT_MESSAGE_ROLES,
  LEVELING_AGENT_WRITE_TOOLS,
} from '../../../../../../constants/levelingAgent';
import { isArrayEmpty } from '../../../../../../utility/helpers';

const Message = ({
  classes,
  translations,
  auth,
  role,
  text,
  reasoningBlocks,
  messageTools,
  isStreaming = false,
  onWriteToolSuccess = undefined,
}) => {
  const isAssistantMessage = role === LEVELING_AGENT_MESSAGE_ROLES.ASSISTANT;
  const isUserMessage = role === LEVELING_AGENT_MESSAGE_ROLES.USER;

  let messageAuthor = <span className={classes.role}>{role}</span>;

  if (isUserMessage && auth?.id) {
    messageAuthor = (
      <UserAvatar
        className={classes.messageAuthor}
        user={auth}
        labelClass={classes.userLabel}
        variant="subtitle1"
        small
        caption
      />
    );
  } else if (isAssistantMessage) {
    messageAuthor = (
      <AssistantAvatar
        translations={translations}
        className={classes.messageAuthor}
      />
    );
  }

  return (
    <div className={classes.message}>
      {messageAuthor}
      {isAssistantMessage && (
        <>
          {reasoningBlocks.map(block => (
            <ReasoningBlock
              key={block.id}
              label={translations.reasoning?.label ?? 'Thinking'}
              text={block.reasoning}
            />
          ))}
          {text &&
            (isStreaming ? (
              <Typography variant="body2" className={classes.messageContent}>
                {text}
              </Typography>
            ) : (
              <Typography
                variant="body2"
                component="div"
                className={classes.messageContent}
              >
                <ReactMarkdown>{text}</ReactMarkdown>
              </Typography>
            ))}
          {!isArrayEmpty(messageTools) && (
            <div className={classes.toolCalls}>
              {messageTools.map(tool => {
                const toolName = tool.call?.name;
                const isWriteTool =
                  LEVELING_AGENT_WRITE_TOOLS.includes(toolName);

                return (
                  <Tool
                    key={tool.id ?? tool.call?.id}
                    translations={translations}
                    tool={tool}
                    onSuccess={isWriteTool ? onWriteToolSuccess : undefined}
                  />
                );
              })}
            </div>
          )}
        </>
      )}
      {!isAssistantMessage && text && (
        <Typography variant="body2" className={classes.messageContent}>
          {text}
        </Typography>
      )}
    </div>
  );
};

Message.propTypes = {
  classes: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired,
  auth: PropTypes.shape({}).isRequired,
  role: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  reasoningBlocks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      reasoning: PropTypes.string.isRequired,
    })
  ).isRequired,
  messageTools: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isStreaming: PropTypes.bool,
  onWriteToolSuccess: PropTypes.func,
};

export default memo(Message, (prevProps, nextProps) => {
  if (prevProps.isStreaming || nextProps.isStreaming) {
    return false;
  }

  if (
    prevProps.role !== nextProps.role ||
    prevProps.text !== nextProps.text ||
    prevProps.messageTools !== nextProps.messageTools
  ) {
    return false;
  }

  if (prevProps.reasoningBlocks.length !== nextProps.reasoningBlocks.length) {
    return false;
  }

  return prevProps.reasoningBlocks.every(
    (block, index) =>
      block.reasoning === nextProps.reasoningBlocks[index]?.reasoning
  );
});
